import { HTTPClient, HTTPMethod, HTTPResponse } from "@/core/common/communication/HTTPClient";
import CalendarEventDS, { IEventExtremesInfos, SerializedCalendarEventDS } from "../domain/CalendarEventDS";
import EventsRepository from "../domain/EventsRepository";
import { LoadEventsResponse, GenericEventOperationResponse } from "../domain/EventOperationsResponse";
import {
  DELETE_EVENT_ERROR,
  LOADING_EVENTS_ERROR,
  SAVE_EVENT_ERROR,
  UPDATE_EVENT_ERROR,
  UPDATE_EVENT_EXTREMES_ERROR
} from "@/core/common/utils/ResourceStrings";
import CalendarEvent from "../domain/CalendarEvent";

interface APIResponse<T> {
  result: boolean;
  data?: T;
  detail?: string | object;
}

interface EventsByRangeRequest {
  startDate: Date;
  endDate: Date;
  filters: Array<string>;
}

interface BackendEvent {
  id: string;
  start: string;
  end: string;
  employeeIds: Array<string>;
  performanceId: string;
  customerId: string;
  cost: number;
  title: string;
  visible: boolean;
  borderColor: string;
  backgroundColor: string;
}

interface CreateOrUpdateBackendEventDto {
  id: string;
  performanceId: string;
  customerId: string;
  employeeIds: Array<string>;
  start: string;
  end: string;
  cost: number;
  visible: boolean;
  title: string;
  borderColor: string;
  backgroundColor: string;
}

export default class EventsRepositoryImpl extends EventsRepository {
  private httpClient: HTTPClient;
  private selectedUsers: Set<string>;

  constructor(httpClient: HTTPClient) {
    super();
    this.httpClient = httpClient;
    this.selectedUsers = new Set();
  }

  private mapFromBackendEvent(event: BackendEvent): SerializedCalendarEventDS {
    return {
      id: event.id,
      start: event.start,
      end: event.end,
      allDay: false,
      employees: event.employeeIds || new Array<string>(),
      performance: event.performanceId,
      customer: event.customerId,
      cost: event.cost,
      title: event.title,
      visible: event.visible,
      borderColor: event.borderColor,
      backgroundColor: event.backgroundColor
    };
  }

  private getDetail(detail: string | object | undefined): string {
    if (!detail) return "";
    return typeof detail === "string" ? detail : JSON.stringify(detail);
  }

  private mapToBackendEventPayload(event: CalendarEventDS): CreateOrUpdateBackendEventDto {
    return {
      id: event.id,
      performanceId: event.performance,
      customerId: event.customer,
      employeeIds: event.employees,
      start: event.start.toISOString(),
      end: event.end.toISOString(),
      cost: event.cost,
      visible: event.visible,
      title: event.title,
      borderColor: event.borderColor,
      backgroundColor: event.backgroundColor
    };
  }

  public async loadEvents(): Promise<LoadEventsResponse> {
    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.GET, "/api/event/list", {});

    const result = {
      events: new Array<SerializedCalendarEventDS>(),
      success: true,
      errorMsg: ""
    };
    const apiResponse = httpResponse.data as APIResponse<Array<BackendEvent>>;

    if (httpResponse.success && apiResponse.result) {
      result.events = (apiResponse.data || new Array<BackendEvent>()).map((event) => this.mapFromBackendEvent(event));
    } else {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${LOADING_EVENTS_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${LOADING_EVENTS_ERROR}${httpResponse.errorMsg}`;
    }

    return Promise.resolve(result);
  }

  private injectFiltersInRequest(request: EventsByRangeRequest) {
    if (this.selectedUsers.size > 0) {
      request.filters = Array.from(this.selectedUsers);
    } else request.filters = new Array<string>();
  }

  public async loadEventsRange(startDate: Date, endDate: Date): Promise<LoadEventsResponse> {
    const eventsByRangeRequest = { startDate: startDate, endDate: endDate, filters: new Array<string>() };
    this.injectFiltersInRequest(eventsByRangeRequest);
    const httpResponse: HTTPResponse = await this.httpClient.send(
      HTTPMethod.POST,
      "/api/event/range",
      eventsByRangeRequest
    );

    const result = {
      events: new Array<SerializedCalendarEventDS>(),
      success: true,
      errorMsg: ""
    };
    const apiResponse = httpResponse.data as APIResponse<Array<BackendEvent>>;
    if (httpResponse.success && apiResponse.result) {
      result.events = (apiResponse.data || new Array<BackendEvent>()).map((event) => this.mapFromBackendEvent(event));
    } else {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${LOADING_EVENTS_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${LOADING_EVENTS_ERROR}${httpResponse.errorMsg}`;
    }

    return Promise.resolve(result);
  }

  public async saveEvent(event: CalendarEventDS): Promise<GenericEventOperationResponse> {
    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/event/create", {
      event: this.mapToBackendEventPayload(event)
    });

    const result = { success: false, errorMsg: "" };
    const apiResponse = httpResponse.data as APIResponse<object>;
    if (httpResponse.success && apiResponse.result) result.success = true;
    else result.errorMsg = `${SAVE_EVENT_ERROR} : ${this.getDetail(apiResponse.detail) || httpResponse.errorMsg}`;
    return Promise.resolve(result);
  }

  public async updateEventExtremes(event: IEventExtremesInfos): Promise<GenericEventOperationResponse> {
    const serializedEvent = CalendarEvent.getInstance().serializeEventExtremes(event);
    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/event/update-extremes", {
      ...serializedEvent
    });

    const result = { success: false, errorMsg: "" };
    const apiResponse = httpResponse.data as APIResponse<object>;
    if (httpResponse.success && apiResponse.result) result.success = true;
    else
      result.errorMsg = `${UPDATE_EVENT_EXTREMES_ERROR} : ${
        this.getDetail(apiResponse.detail) || httpResponse.errorMsg
      }`;
    return Promise.resolve(result);
  }

  public async update(event: CalendarEventDS): Promise<GenericEventOperationResponse> {
    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/event/update", {
      event: this.mapToBackendEventPayload(event)
    });

    const result = { success: false, errorMsg: "" };
    const apiResponse = httpResponse.data as APIResponse<object>;
    if (httpResponse.success && apiResponse.result) result.success = true;
    else result.errorMsg = `${UPDATE_EVENT_ERROR} : ${this.getDetail(apiResponse.detail) || httpResponse.errorMsg}`;
    return Promise.resolve(result);
  }

  public async deleteEvent(eventId: string): Promise<GenericEventOperationResponse> {
    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/event/delete", {
      id: eventId
    });

    const result = { success: false, errorMsg: "" };
    const apiResponse = httpResponse.data as APIResponse<object>;
    if (httpResponse.success && apiResponse.result) result.success = true;
    else result.errorMsg = `${DELETE_EVENT_ERROR} : ${this.getDetail(apiResponse.detail) || httpResponse.errorMsg}`;
    return Promise.resolve(result);
  }

  public getSelectedUsers(): Set<string> {
    return this.selectedUsers;
  }

  public setSelectedUsers(value: Set<string>): void {
    this.selectedUsers = value;
  }
}
